import { inject, Injectable } from '@angular/core';
import { IApiStandardResponse } from '@core/models/api/api.models';
import { OtpChannel } from '@core/models/custom-types.model';
import { IUserApiResponse } from '@core/models/user.model';
import { IVerificaContattiStatus } from '@core/models/verifica.model';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '@core/services/api/api.service';

@Injectable({
  providedIn: 'root',
})
export class VerificaService {
  private apiService = inject(ApiService);

  /**
   * Richiede l'invio di un codice OTP per confermare un contatto.
   *
   * @param type Canale di invio del codice (`email` o `sms`)
   * @returns Risposta standard API dell'operazione
   */
  richiediCodiceConfermaContatto(type: OtpChannel): Observable<IApiStandardResponse<unknown>> {
    return this.apiService.get<IApiStandardResponse<unknown>>(
      `user/contatto/invia-codice-verifica/${type}`
    );
  }

  /**
   * Conferma un contatto (email o cellulare) utilizzando il codice OTP ricevuto.
   *
   * @param codice_numerico Codice OTP numerico ricevuto dall'utente
   * @returns Risposta standard API dell'operazione
   */
  confermaContatto(codice_numerico: number): Observable<IApiStandardResponse<unknown>> {
    return this.apiService.get<IApiStandardResponse<unknown>>(
      `user/contatto/conferma/${codice_numerico}`
    );
  }

  /**
   * Recupera lo stato di verifica dei contatti dell'utente corrente.
   *
   * Il servizio legge l'endpoint `user` e mappa solo i flag necessari
   * per i controlli di accesso lato frontend.
   *
   * @returns Oggetto con `email_is_verified` e `cellulare_is_verified`
   */
  getStatoVerificaContatti(): Observable<IVerificaContattiStatus> {
    return this.apiService.get<IUserApiResponse>('user').pipe(
      map((response) => ({
        email_is_verified: response.data.email_is_verified,
        cellulare_is_verified: response.data.cellulare_is_verified,
      }))
    );
  }

  /**
   * Verifica se email e cellulare risultano entrambi confermati.
   *
   * @param stato Stato corrente di verifica contatti
   * @returns `true` solo se tutti i contatti richiesti sono verificati
   */
  sonoContattiVerificati(stato: IVerificaContattiStatus): boolean {
    return stato.email_is_verified === true && stato.cellulare_is_verified === true;
  }
}
